import React, { useState, useEffect, useRef, useCallback } from 'react';
import P, { InferProps } from 'prop-types';
import { useSnackbar } from 'notistack';
import debounce from 'lodash/debounce';
import set from 'lodash/set';
import firebaseApp from '../../../firebase-app';
import DragDropContainer from './drag-drop-container';
import { useUserAuth } from '../../../contexts/auth';
import { Module, Scene, DEVICES, SCENE, FotbollPlayTeamConfig, ScenePayload } from './types';
import {
  setSceneConfigValues,
  mapInitialSceneConfigValue,
} from '../../../utils/scene-config-helpers';
import { ROLES } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import {
  getAppConfigPath,
  getFotbollPlayTeamConfigPath,
  getSceneConfigPath,
  getUserAppConfigsPath,
} from '../../../utils/paths';
import SceneConfigHeader from './components/scene-config-header';
import { AppConfigData, Promo } from '../../../types/app-config';
import { collection, doc, getDoc } from 'firebase/firestore';
import getSceneModules from './UI-modules';
import { clone } from 'lodash';
import { Box } from '@mui/material';
import ScenePage from '../../common/scene/scene-page';
import adminApi from '../../../api/admin-api';
import { User } from 'firebase/auth';

async function uploadSceneConfig(
  teamAbbrv: string,
  body: { path: string; doc: string; scene: ScenePayload },
  currentUser: User | null
) {
  if (!currentUser) {
    throw new Error('Unauthorized: no authenticated user in request context.');
  }
  const auth = await currentUser?.getIdToken();
  const response = await adminApi.post({
    path: `/app/scene-config/${teamAbbrv}`,
    auth,
    body,
  });
  const responseData = await response.json();

  return responseData;
}
const appconfigUserUpload = debounce(
  async ({ currentUser, mobileScenes, teamAbbrv }) => {
    if (!currentUser || !mobileScenes) {
      return;
    }

    const newMobileScenes = setSceneConfigValues(mobileScenes);

    // Note that we always set the mobile scenes as the tablet scenes as well, for now atleast
    // const newTabletScenes = setSceneConfigValues(mobileScenes);

    try {
      const path = getUserAppConfigsPath(currentUser?.uid);
      const commonData = {
        path,
        scene: newMobileScenes,
      };
      uploadSceneConfig(
        teamAbbrv,
        {
          ...commonData,
          doc: `${teamAbbrv}-${DEVICES.MOBILE}`,
        },
        currentUser
      );
      uploadSceneConfig(
        teamAbbrv,
        {
          ...commonData,
          doc: `${teamAbbrv}-${DEVICES.TABLET}`,
        },
        currentUser
      );
    } catch (error: any) {
      console.log(error.message);
    }
  },
  5000,
  { leading: true }
);

function SceneConfig({ match }: InferProps<typeof SceneConfig.propTypes>) {
  const hasLoaded = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, isSuperAdmin, hasTeamRole } = useUserAuth();
  const { strings } = useStringsContext();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [sceneModules, setSceneModules] = useState<Module[] | null>(null);
  const [mobileScenes, setMobileScenes] = useState<Record<string, Scene> | null>(null);
  const [tabletScenes, setTabletScenes] = useState<Record<string, Scene> | null>(null);
  const [selectedScene, setSelectedScene] = useState<string>(SCENE.STARTSCENE);
  const [selectedDevices, setSelectedDevices] = useState(DEVICES.MOBILE);
  const [appConfigPromos, setAppConfigPromos] = useState<Promo[] | null>(null);

  const teamAbbrv = match.params.id;

  useEffect(() => {
    async function fetchConfig() {
      const mobileAppConfigRef = collection(firebaseApp.db, getSceneConfigPath(teamAbbrv));
      const tabletAppConfigRef = collection(firebaseApp.db, getSceneConfigPath(teamAbbrv));
      const appConfigRef = doc(firebaseApp.db, getAppConfigPath(teamAbbrv));
      const fotbollPlayTeamConfigRef = doc(firebaseApp.db, getFotbollPlayTeamConfigPath(teamAbbrv));

      try {
        const mobileRes = await getDoc(doc(mobileAppConfigRef, DEVICES.MOBILE));
        const tabletRes = await getDoc(doc(tabletAppConfigRef, DEVICES.TABLET));
        const mobileScene = mobileRes.data()?.scene;
        const tabletScene = tabletRes.data()?.scene;

        const appConfig = await getDoc(appConfigRef);
        const appConfigData = appConfig.data() as AppConfigData;
        setAppConfigPromos(appConfigData.promos);

        const fotbollPlayTeamConfig = (await getDoc(fotbollPlayTeamConfigRef)).data() as
          | FotbollPlayTeamConfig
          | undefined;

        const sceneModules = getSceneModules(fotbollPlayTeamConfig?.channels);
        setSceneModules(sceneModules);

        // if existing data is available within app-configs, will fetch from app-configs
        // else will fetch data from "original" teamRef
        if (mobileScene && tabletScene) {
          const updatedMobileScenes = mapInitialSceneConfigValue(
            sceneModules,
            mobileScene,
            appConfigData
          );

          const updatedTabletScenes = mapInitialSceneConfigValue(
            sceneModules,
            tabletScene,
            appConfigData
          );

          setMobileScenes(updatedMobileScenes);
          setTabletScenes(updatedTabletScenes);
        } else {
          console.error('Obselete team scene configs not available!');
        }

        hasLoaded.current = true;
      } catch (e: any) {
        console.log(e.message, e);
      }
    }

    fetchConfig();
  }, [teamAbbrv]);

  const setDevices = (device: DEVICES) => {
    setSelectedDevices(device);
  };

  const handlePublish = useCallback(async () => {
    if (!mobileScenes) {
      return;
    }

    setIsPublishing(true);

    const newMobileScene = setSceneConfigValues(mobileScenes);
    // Note that we always set the mobile scenes as the tablet scenes as well, for now atleast
    // const newTabletScene = setSceneConfigValues(mobileScenes);

    try {
      const path = getSceneConfigPath(teamAbbrv);
      const docMobile = DEVICES.MOBILE;
      const docTablet = DEVICES.TABLET;

      const mobileRes: any = await uploadSceneConfig(
        teamAbbrv,
        {
          path,
          doc: docMobile,
          scene: newMobileScene,
        },
        currentUser
      );
      const tabletRes: any = await uploadSceneConfig(
        teamAbbrv,
        {
          path,
          doc: docTablet,
          scene: newMobileScene,
        },
        currentUser
      );

      let toastMessage = strings.messages.uploadSuccess;
      if (mobileRes.error || tabletRes.error) {
        toastMessage = mobileRes.error || tabletRes.error;
      }

      enqueueSnackbar(toastMessage, {
        variant: mobileRes.error || tabletRes.error ? 'error' : 'success',
      });
    } catch (e: any) {
      console.log(e.message, e);
      enqueueSnackbar(strings.messages.uploadError, {
        variant: 'error',
      });
    } finally {
      setIsPublishing(false);
      setIsDirty(false);
    }
  }, [enqueueSnackbar, mobileScenes, strings, teamAbbrv]);

  const updateDeviceSceneConfig = useCallback(
    (updatedModules: Record<string, Module[]>) => {
      // only runs once initial data has loaded
      if (!hasLoaded.current) return;
      if (!mobileScenes || !tabletScenes) return;

      const updatedMobileScenes = set(
        clone(mobileScenes),
        `${selectedScene}.modules`,
        updatedModules.mobile
      );
      const updatedTabletScenes = set(
        clone(tabletScenes),
        `${selectedScene}.modules`,
        updatedModules.tablet
      );

      setMobileScenes(updatedMobileScenes);
      setTabletScenes(updatedTabletScenes);
      setIsDirty(true);
      appconfigUserUpload({ currentUser, mobileScenes: updatedMobileScenes, teamAbbrv });
    },
    [mobileScenes, selectedScene, tabletScenes, currentUser, teamAbbrv]
  );

  const onChangeScene = (scene: SCENE) => {
    setSelectedScene(scene);
  };

  const onValidation = (isValid: boolean) => {
    setIsValid(isValid);
  };

  if (!mobileScenes || !tabletScenes || !sceneModules) {
    return null;
  }

  const isReadOnly = !isSuperAdmin && hasTeamRole(match.params.id, ROLES.VIEWER);

  return (
    <ScenePage title={strings.appBar.sceneConfigs}>
      <SceneConfigHeader
        isSubmitting={isPublishing}
        isReadOnly={isReadOnly}
        onSubmit={handlePublish}
        sceneTitles={Object.keys(mobileScenes)}
        onChangeScene={onChangeScene}
        isDisabled={!isValid || !isDirty}
      />

      <Box sx={{ mt: 4 }}>
        <DragDropContainer
          setDevices={setDevices}
          selectedDevices={selectedDevices}
          sceneModules={sceneModules}
          modules={{
            mobile: mobileScenes[selectedScene].modules,
            tablet: tabletScenes[selectedScene].modules,
          }}
          updateDeviceSceneConfig={updateDeviceSceneConfig}
          pathParams={match.params.id}
          isReadOnly={isReadOnly}
          onValidation={onValidation}
          promos={appConfigPromos}
        />
      </Box>
    </ScenePage>
  );
}

SceneConfig.propTypes = {
  match: P.shape({
    params: P.shape({
      id: P.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default SceneConfig;
